import {
    CreateTagInput,
    CreateTagMutation,
    CreateTagMutationVariables,
    GetTagQuery,
    ListTagsQuery,
    ListTagsQueryVariables
} from "../../../API";

import { API } from "aws-amplify";
import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLQuery } from "@aws-amplify/api";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { Tag } from "../../../models";
import { TagDAO } from "./TagDAO";
import { createTag } from "../../../graphql/mutations";
import { getTag, listTags } from "../../../graphql/queries";

export class GraphQLTagDAO implements TagDAO {
    private static readonly GET_ATTEMPT_METRIC_NAME = "GraphQLTagDAO.Get.Attempt";
    private static readonly GET_FAILURE_METRIC_NAME = "GraphQLTagDAO.Get.Failure";
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLTagDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLTagDAO.Create.Failure";
    private static readonly LIST_BY_PROPERTY_ID_ATTEMPT_METRIC_NAME = "GraphQLTagDAO.ListByPropertyId.Attempt";
    private static readonly LIST_BY_PROPERTY_ID_FAILURE_METRIC_NAME = "GraphQLTagDAO.ListByPropertyId.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async get(id: string): Promise<Tag> {
        try {
            this.logger.info(
                `Getting Tag record with id: ${id}`,
                undefined,
                [GraphQLTagDAO.GET_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result = await API.graphql<GraphQLQuery<GetTagQuery>>({
                query: getTag,
                variables: { id },
                authToken
            });
            return result.data?.getTag as Tag;
        } catch (error) {
            this.logger.error(
                `Failed to get Tag record with id: ${id}`,
                error,
                [GraphQLTagDAO.GET_FAILURE_METRIC_NAME]
            );
            throw error;
        }
    }

    public async create(tag: CreateTagInput): Promise<Tag> {
        try {
            this.logger.info(
                `Creating Tag record with input: ${JSON.stringify(tag)}`,
                tag,
                [GraphQLTagDAO.CREATE_ATTEMPT_METRIC_NAME]
            );
            const variables: CreateTagMutationVariables = { input: tag };
            const authToken: string = await this.idTokenSupplier.get();
            const result = await API.graphql<GraphQLQuery<CreateTagMutation>>({
                query: createTag,
                variables,
                authToken
            });
            return result.data?.createTag as Tag;
        } catch (error) {
            this.logger.error(
                `Failed to create Tag record with input: ${JSON.stringify(tag)}`,
                error,
                [GraphQLTagDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw error;
        }
    }

    public async listByPropertyId(propertyId: string): Promise<Tag[]> {
        try {
            this.logger.info(
                `Listing Tags by PropertyId: ${propertyId}`,
                undefined,
                [GraphQLTagDAO.LIST_BY_PROPERTY_ID_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result = await API.graphql<GraphQLQuery<ListTagsQuery>>({
                query: listTags,
                variables: {
                    filter: {
                        and: [
                            {
                                propertyId: { eq: propertyId },
                            }, {
                                _deleted: { ne: true }
                            }
                        ]
                    }
                } satisfies ListTagsQueryVariables,
                authToken
            });
            return (result.data?.listTags?.items ?? [])as Tag[];
        } catch (error) {
            this.logger.error(
                `Failed to list Tags by PropertyId: ${propertyId}`,
                error,
                [GraphQLTagDAO.LIST_BY_PROPERTY_ID_FAILURE_METRIC_NAME]
            );
            throw error;
        }
    }
}

export const graphQLTagDAO: TagDAO = new GraphQLTagDAO(
    ClientLoggerFactory.getClientLogger("GraphQLTagDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);